import React from 'react';
import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { createOfferCurrency as currency } from 'common/oldJavascripts/utils/format';
import isNonUnionWorkScheduleA from 'common/utilities/isNonUnionWorkScheduleA';
import useFeatureFlags from 'common/hooks/useFeatureFlags';
import UsaOnly from 'common/components/UsaOnly';
import CanadaOnly from 'common/components/CanadaOnly';
import useOfferCountry from 'common/components/SpecifiedCountryOnly/useOfferCountry';

const formatAmount = amount => {
  return !Number.isInteger(amount) ? currency(amount) : `$${amount}`;
};

const mixedEmployeeInfoStyles = makeStyles(theme => ({
  title: {
    color: '#999999',
    fontSize: 12,
    fontWeight: 400,
  },
  name: {
    fontSize: 24,
    fontWeight: 400,
    color: '#646464',
  },
  date: {
    fontSize: 14,
    color: '#646464',
    fontWeight: 400,
  },
  email: {
    fontSize: 10,
    color: '#0091ff',
    fontWeight: 400,
    marginTop: '3px',
    letterSpacing: '0.3px',
  },
  spacing: {
    marginTop: '20px',
    marginBottom: '10px',
  },
  amount: {
    color: '#2eaba4',
    fontSize: 24,
    fontWeight: 400,
    marginRight: '10px',
  },
  description: {
    wordBreak: 'break-word',
  },
  flag: {
    display: 'block',
    margin: 'auto',
    width: 24,
    height: 24,
  },
  caption: {
    color: '#aaaaaa',
    fontSize: 10,
    fontWeight: 400,
    padding: 0,
    textAlign: 'center',
  },
}));

const MixedEmployeeTerms = ({ offer }) => {
  const classes = mixedEmployeeInfoStyles();
  const { hourlyRate, weeklyRate, dailyRate } = getRate(offer);
  const workScheduleCode = offer?.work_schedule_code;
  const flags = useFeatureFlags();
  const { data: offerCountry } = useOfferCountry({ offerId: offer?.id });
  const isCanada = offerCountry?.code === 'CA';
  const isNonUnion = offer?.union_is_non_union;
  const { terms_of_employment = {} } = offer;
  const { id: offerId = null } = offer;

  const {
    overtime_rate: overtimeRate,
    pay_6th_day_rate: sixthDayRate,
    pay_7th_day_rate: seventhDayRate,
    guaranteed_hours_studio: hoursPerDay,
  } = terms_of_employment;

  const isNonUnionScheduleAFlagActive = flags.includes('nonUnionSchedules');
  const isNonUnionScheduleCFlagActive = flags.includes('nonUnionScheduleC');
  const isNonUnionScheduleBFlagActive = flags.includes('nonUnionScheduleB');

  const isNonUnionScheduleA =
    isNonUnion &&
    isNonUnionScheduleAFlagActive &&
    isNonUnionWorkScheduleA(workScheduleCode);
  const isNonUnionScheduleB =
    isNonUnion && isNonUnionScheduleBFlagActive && workScheduleCode === 'B';
  const isNonUnionScheduleC =
    isNonUnion &&
    isNonUnionScheduleCFlagActive &&
    ['C', 'E', 'CN'].includes(workScheduleCode);

  const payDayTitle = day => {
    if (isNonUnionScheduleA) {
      return (
        <>
          Apply Hours per <br /> Day to {day}
          th Day
        </>
      );
    }
    if (isNonUnionScheduleC) {
      return `Pay ${day}th day at`;
    }
    return `${day}th day pay`;
  };

  const weekendPayRate = payRate => {
    if (isNonUnionScheduleA) return payRate > 0 ? 'Yes' : 'No';
    return formatAmount(payRate);
  };

  const isHourlySchedule = isNonUnionScheduleA || isNonUnionScheduleB;
  const hoursTitle =
    isNonUnionScheduleA || isCanada ? 'Hours per Day' : 'Hours per Week';
  const hoursPerWeekTitle =
    isCanada && terms_of_employment?.guaranteed_hours_location
      ? 'Hours Per Week'
      : null;
  const hoursPerWeek = isCanada
    ? terms_of_employment?.guaranteed_hours_location
    : null;

  return (
    <>
      <Grid container className={classes.spacing}>
        {weeklyRate && (
          <Grid item xs={4}>
            <Typography
              variant="h6"
              className={classes.title}
              data-test-id={`EmployeeInfo-weeklyRateTitle-${offerId}`}
            >
              Weekly Rate
            </Typography>
            <Typography
              className={classes.amount}
              data-test-id={`EmployeeInfo-weeklyRate-${offerId}`}
            >
              {currency(weeklyRate)}
            </Typography>
          </Grid>
        )}
        {hourlyRate && (
          <Grid item xs={4}>
            <Typography
              variant="h6"
              className={classes.title}
              data-test-id={`EmployeeInfo-hourlyRateTitle-${offerId}`}
            >
              <UsaOnly>Hourly</UsaOnly>
              <CanadaOnly>Rate Per Hour</CanadaOnly>
            </Typography>
            <Typography
              className={classes.amount}
              data-test-id={`EmployeeInfo-hourlyRate-${offerId}`}
            >
              {currency(hourlyRate)}
            </Typography>
          </Grid>
        )}
        {dailyRate && (
          <Grid item xs={4}>
            <Typography
              variant="h6"
              className={classes.title}
              data-test-id={`EmployeeInfo-dailyRateTitle-${offerId}`}
            >
              Daily
            </Typography>
            <Typography
              className={classes.amount}
              data-test-id={`EmployeeInfo-dailyRateAmount-${offerId}`}
            >
              {currency(dailyRate)}
            </Typography>
          </Grid>
        )}
        {(isHourlySchedule || isCanada) && (
          <>
            {overtimeRate && (
              <Grid item xs={4}>
                <Typography
                  variant="h6"
                  className={classes.title}
                  data-test-id={`EmployeeInfo-overtimeRateTitle-${offerId}`}
                >
                  Overtime Rate
                </Typography>
                <Typography
                  className={classes.amount}
                  data-test-id={`EmployeeInfo-overtimeRateAmount-${offerId}`}
                >
                  {currency(overtimeRate)}
                </Typography>
              </Grid>
            )}
            {hoursPerDay && (
              <Grid item xs={4}>
                <Typography
                  variant="h6"
                  className={classes.title}
                  data-test-id={`EmployeeInfo-hoursPerDayTitle-${offerId}`}
                >
                  {hoursTitle}
                </Typography>
                <Typography
                  className={classes.amount}
                  data-test-id={`EmployeeInfo-hoursPerDay-${offerId}`}
                >
                  {hoursPerDay}
                </Typography>
              </Grid>
            )}
          </>
        )}
      </Grid>
      <Grid container className={classes.spacing}>
        {hoursPerWeek && (
          <CanadaOnly>
            <Grid item xs={4}>
              <Typography
                variant="h6"
                className={classes.title}
                data-test-id={`EmployeeInfo-hoursPerWeekTitle-${offerId}`}
              >
                {hoursPerWeekTitle}
              </Typography>
              <Typography
                className={classes.amount}
                data-test-id={`EmployeeInfo-hoursPerWeek-${offerId}`}
              >
                {hoursPerWeek}
              </Typography>
            </Grid>
          </CanadaOnly>
        )}
        {sixthDayRate && (
          <Grid item xs={4}>
            <Typography
              variant="h6"
              className={classes.title}
              data-test-id={`EmployeeInfo-sixthDayRateTitle-${offerId}`}
            >
              {payDayTitle('6')}
            </Typography>
            <Typography
              className={classes.description}
              data-test-id={`EmployeeInfo-sixthDayRate-${offerId}`}
            >
              {weekendPayRate(sixthDayRate)}
            </Typography>
          </Grid>
        )}
        {seventhDayRate && (
          <Grid item xs={4}>
            <Typography
              variant="h6"
              className={classes.title}
              data-test-id={`EmployeeInfo-seventhDayRateTitle-${offerId}`}
            >
              {payDayTitle('7')}
            </Typography>
            <Typography
              className={classes.description}
              data-test-id={`EmployeeInfo-seventhDayRate-${offerId}`}
            >
              {weekendPayRate(seventhDayRate)}
            </Typography>
          </Grid>
        )}
      </Grid>
    </>
  );
};
const getRate = offer => {
  const { terms_of_employment = {} } = offer;
  const {
    rate_per_week_studio,
    rate_per_week_location,
    rate_per_day_studio,
    rate_per_hour_location,
    rate_per_hour_studio,
  } = terms_of_employment;

  return {
    hourlyRate: rate_per_hour_studio || rate_per_hour_location,
    dailyRate: rate_per_day_studio || rate_per_day_studio,
    weeklyRate: rate_per_week_studio || rate_per_week_location,
  };
};
export default MixedEmployeeTerms;
